import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Youtube, AlertCircle, ExternalLink, Music, Globe } from 'lucide-react';
import type { OnboardingStepProps } from '../onboardingSteps';
import { resolveYouTubeChannel, searchYouTubeChannels, previewWikipediaUrl, previewSpotifyUrl, type YouTubeSearchResult } from '../onboardingApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

/** YouTube Official Artist Channel music note badge */
function MusicBadge() {
  return (
    <svg
      className="w-3.5 h-3.5 inline-block shrink-0"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-label="Official Artist Channel"
    >
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 14.5c-2.49 0-4.5-2.01-4.5-4.5S9.51 7.5 12 7.5s4.5 2.01 4.5 4.5-2.01 4.5-4.5 4.5zm0-5.5c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z" />
    </svg>
  );
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toLocaleString();
}

function isWikipediaUrl(value: string): boolean {
  if (!value.trim()) return true;
  try {
    const url = new URL(value.trim());
    return url.hostname.replace(/^www\./, '') === 'en.wikipedia.org'
      && url.pathname.startsWith('/wiki/')
      && url.pathname.length > '/wiki/'.length;
  } catch {
    return false;
  }
}

function isSpotifyArtistUrl(value: string): boolean {
  if (!value.trim()) return true;
  try {
    const url = new URL(value.trim());
    const parts = url.pathname.split('/').filter(Boolean);
    return url.hostname.replace(/^www\./, '') === 'open.spotify.com'
      && parts.includes('artist');
  } catch {
    return false;
  }
}

interface ResolvedChannel {
  channelId: string;
  title: string;
  thumbnail: string | null;
  customUrl: string | null;
  subscriberCount: number | null;
  videoCount: number | null;
}

interface ResolvedWikipedia {
  title: string;
  url: string;
  description?: string;
  image?: string;
}

interface ResolvedSpotify {
  artistId: string;
  name: string;
  url: string;
  image?: string;
  followers?: number | null;
  genres?: string[];
}

export interface YouTubeStepResult {
  channelInput: string;
  channel: ResolvedChannel;
  channelOnly: boolean;
  wikipediaUrl?: string;
  spotifyUrl?: string;
}

interface YouTubeOnboardingStepProps extends OnboardingStepProps {
  onResolved: (result: YouTubeStepResult) => void;
  onSkip: () => void;
}

const YouTubeOnboardingStep: React.FC<YouTubeOnboardingStepProps> = ({
  saveRef,
  onValidationChange,
  onResolved,
  onSkip: _onSkip,
}) => {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resolved, setResolved] = useState<ResolvedChannel | null>(null);
  const [channelOnly, setChannelOnly] = useState(false);
  const [wikipedia, setWikipedia] = useState<ResolvedWikipedia | null>(null);
  const [spotify, setSpotify] = useState<ResolvedSpotify | null>(null);
  const [wikipediaUrl, setWikipediaUrl] = useState('');
  const [spotifyUrl, setSpotifyUrl] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wikipediaManuallyEdited = useRef(false);
  const spotifyManuallyEdited = useRef(false);
  // Preserve auto-resolved data so it can be restored when the user
  // re-enters the same URL after clearing it.
  const autoResolvedWikipedia = useRef<ResolvedWikipedia | null>(null);
  const autoResolvedSpotify = useRef<ResolvedSpotify | null>(null);
  const wikipediaUrlValid = isWikipediaUrl(wikipediaUrl);
  const spotifyUrlValid = isSpotifyArtistUrl(spotifyUrl);

  // Manual Wikipedia URL preview state
  const [wikiPreviewLoading, setWikiPreviewLoading] = useState(false);
  const [wikiPreviewFailed, setWikiPreviewFailed] = useState(false);
  const wikiPreviewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wikiPreviewSeq = useRef(0);

  // Manual Spotify URL preview state
  const [spotifyPreviewLoading, setSpotifyPreviewLoading] = useState(false);
  const [spotifyPreviewFailed, setSpotifyPreviewFailed] = useState(false);
  const spotifyPreviewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const spotifyPreviewSeq = useRef(0);

  // Search state — for "search by name" mode
  const [searchResults, setSearchResults] = useState<YouTubeSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // When user edits the Wikipedia URL:
  // - If it matches the auto-resolved page, restore the resolved data.
  // - If it's a different, well-formed Wikipedia URL (manual paste), fetch a
  //   preview from the backend so the page is verified and shown before build.
  // - Otherwise clear the stale card.
  useEffect(() => {
    if (wikiPreviewDebounceRef.current) clearTimeout(wikiPreviewDebounceRef.current);
    const seq = ++wikiPreviewSeq.current;
    setWikiPreviewFailed(false);

    const auto = autoResolvedWikipedia.current;
    if (auto && wikipediaUrl === auto.url) {
      setWikipedia(auto);
      setWikiPreviewLoading(false);
      return;
    }

    // URL no longer matches the displayed card → clear stale data
    setWikipedia((prev) => (prev && wikipediaUrl !== prev.url ? null : prev));

    const trimmed = wikipediaUrl.trim();
    if (!trimmed || !isWikipediaUrl(trimmed)) {
      setWikiPreviewLoading(false);
      return;
    }

    wikiPreviewDebounceRef.current = setTimeout(async () => {
      setWikiPreviewLoading(true);
      try {
        const preview = await previewWikipediaUrl(trimmed, resolved?.title);
        if (seq !== wikiPreviewSeq.current) return; // stale response
        if (preview) {
          setWikipedia(preview);
          setWikiPreviewFailed(false);
        } else {
          setWikipedia(null);
          setWikiPreviewFailed(true);
        }
      } catch {
        if (seq === wikiPreviewSeq.current) setWikiPreviewFailed(true);
      } finally {
        if (seq === wikiPreviewSeq.current) setWikiPreviewLoading(false);
      }
    }, 700);

    return () => {
      if (wikiPreviewDebounceRef.current) clearTimeout(wikiPreviewDebounceRef.current);
    };
  }, [wikipediaUrl, resolved]);

  // Same for Spotify: restore auto-resolved data when URL matches; fetch a
  // preview for a manually pasted artist URL; clear stale data otherwise.
  useEffect(() => {
    if (spotifyPreviewDebounceRef.current) clearTimeout(spotifyPreviewDebounceRef.current);
    const seq = ++spotifyPreviewSeq.current;
    setSpotifyPreviewFailed(false);

    const auto = autoResolvedSpotify.current;
    if (auto && spotifyUrl === auto.url) {
      setSpotify(auto);
      setSpotifyPreviewLoading(false);
      return;
    }

    // URL no longer matches the displayed card → clear stale data
    setSpotify((prev) => (prev && spotifyUrl !== prev.url ? null : prev));

    const trimmed = spotifyUrl.trim();
    if (!trimmed || !isSpotifyArtistUrl(trimmed)) {
      setSpotifyPreviewLoading(false);
      return;
    }

    spotifyPreviewDebounceRef.current = setTimeout(async () => {
      setSpotifyPreviewLoading(true);
      try {
        const preview = await previewSpotifyUrl(trimmed);
        if (seq !== spotifyPreviewSeq.current) return; // stale response
        if (preview) {
          setSpotify(preview);
          setSpotifyPreviewFailed(false);
        } else {
          setSpotify(null);
          setSpotifyPreviewFailed(true);
        }
      } catch {
        if (seq === spotifyPreviewSeq.current) setSpotifyPreviewFailed(true);
      } finally {
        if (seq === spotifyPreviewSeq.current) setSpotifyPreviewLoading(false);
      }
    }, 700);

    return () => {
      if (spotifyPreviewDebounceRef.current) clearTimeout(spotifyPreviewDebounceRef.current);
    };
  }, [spotifyUrl]);

  // Dismiss search results when clicking outside the search container
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setShowSearch(false);
      }
    };
    if (showSearch) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSearch]);

  // Detect whether input looks like a URL/handle or a search query.
  // URLs/handles trigger the existing resolveChannel flow.
  // Plain text triggers YouTube channel search.
  const looksLikeUrlOrHandle = (value: string): boolean => {
    const t = value.trim();
    if (!t) return false;
    if (t.startsWith('http://') || t.startsWith('https://')) return true;
    if (t.startsWith('@')) return true;
    if (/^UC[\w-]{22}$/.test(t)) return true;
    if (t.includes('youtube.com') || t.includes('youtu.be')) return true;
    return false;
  };

  // Search YouTube for channels by name (debounced)
  const searchYouTube = useCallback(async (query: string) => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      setShowSearch(false);
      return;
    }
    setIsSearching(true);
    setShowSearch(true);
    try {
      const results = await searchYouTubeChannels(trimmed, 5);
      setSearchResults(results);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // No save action for this step — data is passed via onResolved
  useEffect(() => {
    saveRef.current = async () => {
      if (resolved) {
        onResolved({
          channelInput: input.trim(),
          channel: resolved,
          channelOnly,
          // Send empty strings (not undefined) when the user clears a field,
          // so the backend knows the source was explicitly removed and must
          // not be re-discovered during the build.
          wikipediaUrl: wikipediaUrl.trim(),
          spotifyUrl: spotifyUrl.trim(),
        });
      }
    };
  }, [saveRef, resolved, input, wikipediaUrl, spotifyUrl, channelOnly, onResolved]);

  // Valid once channel is resolved and optional source URLs are well-formed.
  useEffect(() => {
    onValidationChange(!!resolved && wikipediaUrlValid && spotifyUrlValid);
  }, [resolved, wikipediaUrlValid, spotifyUrlValid, onValidationChange]);

  const resolveChannel = useCallback(async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setResolved(null);
      setWikipedia(null);
      setSpotify(null);
      setWikipediaUrl('');
      setSpotifyUrl('');
      setError('');
      wikipediaManuallyEdited.current = false;
      spotifyManuallyEdited.current = false;
      autoResolvedWikipedia.current = null;
      autoResolvedSpotify.current = null;
      return;
    }

    setLoading(true);
    setError('');
    setResolved(null);
    setWikipedia(null);
    setSpotify(null);
    setWikipediaUrl('');
    setSpotifyUrl('');
    wikipediaManuallyEdited.current = false;
    spotifyManuallyEdited.current = false;
    autoResolvedWikipedia.current = null;
    autoResolvedSpotify.current = null;

    try {
      const data = await resolveYouTubeChannel(trimmed);
      if (data.success) {
        setResolved({
          channelId: data.channelId,
          title: data.title,
          thumbnail: data.thumbnail,
          customUrl: data.customUrl || null,
          subscriberCount: data.subscriberCount ?? null,
          videoCount: data.videoCount ?? null,
        });
        setWikipedia(data.wikipedia || null);
        setSpotify(data.spotify || null);
        autoResolvedWikipedia.current = data.wikipedia || null;
        autoResolvedSpotify.current = data.spotify || null;
        // Only auto-populate URLs if the user hasn't manually edited them
        if (!wikipediaManuallyEdited.current) {
          setWikipediaUrl(data.wikipedia?.url || '');
        }
        if (!spotifyManuallyEdited.current) {
          setSpotifyUrl(data.spotify?.url || '');
        }
      } else {
        setError(data.message || 'Could not find that channel.');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Could not resolve channel. Please check and try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  // Select a search result → fill input and resolve
  const selectSearchResult = useCallback((result: YouTubeSearchResult) => {
    setShowSearch(false);
    setSearchResults([]);
    // Set input to the channel URL so the resolved card shows correctly
    const channelUrl = result.handle
      ? `https://www.youtube.com/${result.handle}`
      : `https://www.youtube.com/channel/${result.channelId}`;
    setInput(channelUrl);
    // Trigger the existing resolve flow
    resolveChannel(channelUrl);
  }, [resolveChannel]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInput(value);
    setResolved(null);
    setWikipedia(null);
    setSpotify(null);
    setWikipediaUrl('');
    setSpotifyUrl('');
    setError('');
    wikipediaManuallyEdited.current = false;
    spotifyManuallyEdited.current = false;
    autoResolvedWikipedia.current = null;
    autoResolvedSpotify.current = null;

    // Clear both debounce timers
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    const trimmed = value.trim();
    if (trimmed.length > 2) {
      if (looksLikeUrlOrHandle(trimmed)) {
        // URL/handle mode → existing resolve flow
        setShowSearch(false);
        setSearchResults([]);
        debounceRef.current = setTimeout(() => resolveChannel(value), 800);
      } else {
        // Search mode → YouTube channel search
        searchDebounceRef.current = setTimeout(() => searchYouTube(value), 600);
      }
    } else {
      setShowSearch(false);
      setSearchResults([]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      const trimmed = input.trim();
      if (looksLikeUrlOrHandle(trimmed)) {
        resolveChannel(input);
      } else if (trimmed.length >= 2) {
        searchYouTube(input);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* YouTube — input + resolved card in one section */}
      <div className="space-y-1.5" ref={searchContainerRef}>
        <Label>
          Official YouTube channel
        </Label>
        <div className="relative">
          <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Search by name or paste a YouTube link..."
            className="pl-10"
          />
        </div>
        {!resolved && !loading && !showSearch && (
          <p className="text-xs text-muted-foreground">
            Type an artist name to search, or paste a YouTube channel link / @handle
          </p>
        )}

        {/* Search results dropdown */}
        {showSearch && (
          <div className="rounded-md border border-border/60 bg-card shadow-md overflow-hidden mt-2">
            {isSearching ? (
              <div className="px-4 py-3 space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3 animate-pulse">
                    <div className="w-10 h-10 rounded-full bg-muted shrink-0" />
                    <div className="space-y-1.5 flex-1">
                      <div className="h-3.5 bg-muted rounded w-32" />
                      <div className="h-2.5 bg-muted rounded w-24" />
                    </div>
                  </div>
                ))}
              </div>
            ) : searchResults.length > 0 ? (
              <ul className="py-1">
                {searchResults.map((result) => (
                  <li key={result.channelId}>
                    <button
                      type="button"
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => selectSearchResult(result)}
                    >
                      {result.thumbnail ? (
                        <img
                          src={result.thumbnail}
                          alt={result.title}
                          className="w-10 h-10 rounded-full object-cover shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <Youtube className="w-4 h-4 text-muted-foreground" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">{result.title}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {[
                            result.handle,
                            result.subscriberCount != null && `${formatCount(result.subscriberCount)} subscribers`,
                            result.videoCount != null && `${result.videoCount.toLocaleString()} videos`,
                          ].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                No channels found. Try a different name or paste a link.
              </div>
            )}
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className="rounded-md border border-border/60 bg-card shadow-sm overflow-hidden animate-pulse mt-3">
            <div className="px-5 pt-6 pb-5 flex flex-col items-center gap-3">
              <div className="w-18 h-18 rounded-full bg-muted" />
              <div className="space-y-2 flex items-center flex-col">
                <div className="h-4 bg-muted rounded w-32" />
                <div className="h-3 bg-muted rounded w-44" />
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mt-3">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-red-700">{error}</p>
              <Button
                type="button"
                variant="link"
                onClick={() => resolveChannel(input)}
                className="text-sm text-red-600 underline hover:text-red-800 mt-1 p-0 h-auto"
              >
                Try again
              </Button>
            </div>
          </div>
        )}

        {/* Resolved — channel card */}
        {resolved && !loading && (
          <div className="rounded-md border border-border/60 bg-card shadow-sm overflow-hidden mt-3">
            <div className="px-5 pt-6 pb-5 flex flex-col items-center text-center">
              {/* Avatar */}
              {resolved.thumbnail ? (
                <img
                  src={resolved.thumbnail}
                  alt={resolved.title}
                  className="w-18 h-18 rounded-full object-cover"
                />
              ) : (
                <div className="w-18 h-18 rounded-full bg-muted flex items-center justify-center">
                  <Youtube className="w-7 h-7 text-muted-foreground" />
                </div>
              )}

              {/* Name + badge */}
              <div className="mt-3 flex items-center gap-1.5">
                <p className="text-sm font-semibold text-foreground leading-tight truncate max-w-50">
                  {resolved.title}
                </p>
                <span className="text-muted-foreground" title="Music channel">
                  <MusicBadge />
                </span>
              </div>

              {/* Handle + stats as inline text */}
              <p className="mt-1 text-xs text-muted-foreground">
                {[
                  resolved.customUrl,
                  resolved.subscriberCount != null && `${formatCount(resolved.subscriberCount)} subscribers`,
                  resolved.videoCount != null && `${resolved.videoCount.toLocaleString()} videos`,
                ].filter(Boolean).join('  •  ')}
              </p>

              {/* View channel button */}
              <a
                href={`https://youtube.com/${resolved.customUrl || `channel/${resolved.channelId}`}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                View channel
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}
      </div>

      {resolved && !loading && (
        <div className="flex items-start justify-between gap-4 rounded-md border border-border/60 p-4">
          <div className="space-y-1">
            <Label htmlFor="channel-only-discovery">
              Only discover showcase songs from this channel
            </Label>
            <p id="channel-only-discovery-description" className="text-xs text-muted-foreground">
              When enabled, songs uploaded by labels or other YouTube channels won't be included.
            </p>
          </div>
          <Switch
            id="channel-only-discovery"
            checked={channelOnly}
            onCheckedChange={setChannelOnly}
            aria-describedby="channel-only-discovery-description"
          />
        </div>
      )}

      {/* Spotify — peer section, only after YouTube is resolved */}
      {resolved && !loading && (
        <div className="space-y-1.5">
          <Label>Official Spotify account</Label>
          <div className="relative">
            <Music className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1DB954] pointer-events-none" />
            <Input
              value={spotifyUrl}
              onChange={(e) => {
                setSpotifyUrl(e.target.value);
                spotifyManuallyEdited.current = true;
              }}
              placeholder="https://open.spotify.com/artist/..."
              className={`pl-10 ${!spotifyUrlValid ? 'border-red-300 focus-visible:ring-red-400' : ''}`}
            />
          </div>
          <p className={`text-xs ${spotifyUrlValid ? 'text-muted-foreground' : 'text-red-600'}`}>
            {spotifyUrlValid
              ? (spotifyUrl.trim() ? '' : 'Used for About image. Optional.')
              : 'Paste a Spotify artist link, not a track or album link.'}
          </p>

          {/* Manual URL verification states */}
          {spotifyPreviewLoading && (
            <p className="text-xs text-muted-foreground">Checking Spotify artist…</p>
          )}
          {spotifyPreviewFailed && !spotifyPreviewLoading && (
            <p className="text-xs text-amber-600">
              Couldn't verify this Spotify artist — check the link. Unverified links are ignored during setup.
            </p>
          )}

          {/* Spotify resolved card */}
          {spotify && spotifyUrl && spotifyUrlValid && (
            <div className="rounded-md border border-border/60 bg-card shadow-sm overflow-hidden mt-3">
              <div className="px-5 pt-5 pb-5 flex flex-col items-center text-center">
                {spotify.image ? (
                  <img src={spotify.image} alt={spotify.name} className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#1DB954]/10 flex items-center justify-center">
                    <Music className="w-6 h-6 text-[#1DB954]" />
                  </div>
                )}
                <p className="mt-2.5 text-sm font-semibold text-foreground leading-tight truncate max-w-50">
                  {spotify.name}
                </p>
                {spotify.genres && spotify.genres.length > 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {spotify.genres.slice(0, 2).join(', ')}
                  </p>
                )}
                <a
                  href={spotifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  View on Spotify
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Wikipedia — peer section, only after YouTube is resolved */}
      {resolved && !loading && (
        <div className="space-y-1.5">
          <Label>Wikipedia page</Label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              value={wikipediaUrl}
              onChange={(e) => {
                setWikipediaUrl(e.target.value);
                wikipediaManuallyEdited.current = true;
              }}
              placeholder="https://en.wikipedia.org/wiki/Artist_Name"
              className={`pl-10 ${!wikipediaUrlValid ? 'border-red-300 focus-visible:ring-red-400' : ''}`}
            />
          </div>
          <p className={`text-xs ${wikipediaUrlValid ? 'text-muted-foreground' : 'text-red-600'}`}>
            {wikipediaUrlValid
              ? (wikipediaUrl.trim() ? '' : 'Used for content only. Optional.')
              : 'Paste an English Wikipedia page link.'}
          </p>

          {/* Manual URL verification states */}
          {wikiPreviewLoading && (
            <p className="text-xs text-muted-foreground">Checking Wikipedia page…</p>
          )}
          {wikiPreviewFailed && !wikiPreviewLoading && (
            <p className="text-xs text-amber-600">
              Couldn't verify this Wikipedia page — make sure it's the artist's page. Unverified pages are ignored during setup.
            </p>
          )}

          {/* Wikipedia resolved card — only show when backend returned Wikipedia data */}
          {wikipedia && (
            <div className="rounded-md border border-border/60 bg-card shadow-sm overflow-hidden mt-3">
              <div className="px-5 pt-5 pb-5 flex flex-col items-center text-center">
                {wikipedia.image ? (
                  <img src={wikipedia.image} alt={wikipedia.title} className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                    <Globe className="w-6 h-6 text-muted-foreground" />
                  </div>
                )}
                <p className="mt-2.5 text-sm font-semibold text-foreground leading-tight truncate max-w-50">
                  {wikipedia.title}
                </p>
                {wikipedia.description && (
                  <p className="mt-1 text-xs text-muted-foreground">{wikipedia.description}</p>
                )}
                {/* Show "View on Wikipedia" link if we have a valid URL,
                    otherwise construct a search URL from the title. */}
                {wikipediaUrlValid && wikipediaUrl.trim() ? (
                  <a
                    href={wikipediaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    View on Wikipedia
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <a
                    href={`https://en.wikipedia.org/w/index.php?search=${encodeURIComponent(wikipedia.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                  >
                    Search Wikipedia
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Skip hint */}
      {/* <p className="text-xs text-muted-foreground text-center">
        Don't have a YouTube channel?{' '}
        <button type="button" onClick={onSkip} className="text-primary underline hover:text-primary/80">
          Skip this step
        </button>
      </p> */}
    </div>
  );
};

export default YouTubeOnboardingStep;
